// Weekly consolidated report + supervised learning (setter spec §07.5–§07.10, §13.4, C13). Platform-level only:
// report + delivery settings need platform_integration_admin; the learning queue needs platform_prompt_admin (its
// outcome is a prompt/knowledge draft). Schemas in api-weekly-report.ts.
import { z } from 'zod';
import { OkSchema } from './api.js';
import * as R from './api-weekly-report.js';
import { IdParam, items, route } from './route-def.js';

const report = 'platform_integration_admin' as const;
const learning = 'platform_prompt_admin' as const;
const Run = z.object({ runId: IdParam });
const Proposal = z.object({ proposalId: IdParam });

export const weeklyReportRoutes = {
  getWeeklyReportSettings: route({ method: 'GET', path: '/platform/weekly-report/settings', summary: 'Programación y destinos del reporte semanal', tag: 'platform', capability: report, response: R.WeeklyReportSettingsSchema }),
  putWeeklyReportSettings: route({
    method: 'PUT', path: '/platform/weekly-report/settings', summary: 'Guardar programación del reporte semanal', tag: 'platform',
    capability: report, body: R.PutWeeklyReportSettingsSchema, response: R.WeeklyReportSettingsSchema,
  }),
  connectWeeklyReportNotion: route({
    method: 'PUT', path: '/platform/weekly-report/notion', summary: 'Conectar Notion (token cifrado + base de datos)', tag: 'platform',
    capability: report, body: R.ConnectNotionSchema, response: R.WeeklyReportSettingsSchema,
  }),
  testWeeklyReportNotion: route({ method: 'POST', path: '/platform/weekly-report/notion/test', summary: 'Probar acceso a la base de datos de Notion', tag: 'platform', capability: report, response: R.DestinationTestResultSchema }),
  deleteWeeklyReportNotion: route({ method: 'DELETE', path: '/platform/weekly-report/notion', summary: 'Desconectar Notion', tag: 'platform', capability: report, response: OkSchema }),
  connectWeeklyReportSlack: route({
    method: 'PUT', path: '/platform/weekly-report/slack', summary: 'Conectar el Incoming Webhook de Slack del reporte (secreto cifrado)', tag: 'platform',
    capability: report, body: R.ConnectWeeklySlackSchema, response: R.WeeklyReportSettingsSchema,
  }),
  testWeeklyReportSlack: route({ method: 'POST', path: '/platform/weekly-report/slack/test', summary: 'Enviar un mensaje de prueba a Slack', tag: 'platform', capability: report, response: R.DestinationTestResultSchema }),
  deleteWeeklyReportSlack: route({ method: 'DELETE', path: '/platform/weekly-report/slack', summary: 'Desconectar Slack del reporte', tag: 'platform', capability: report, response: OkSchema }),
  listWeeklyReportRuns: route({ method: 'GET', path: '/platform/weekly-report/runs', summary: 'Historial de reportes semanales', tag: 'platform', capability: report, response: items(R.WeeklyReportRunSchema) }),
  getWeeklyReportRun: route({ method: 'GET', path: '/platform/weekly-report/runs/:runId', summary: 'Reporte semanal generado', tag: 'platform', capability: report, params: Run, response: R.WeeklyReportRunDetailSchema }),
  createWeeklyReportRun: route({
    method: 'POST', path: '/platform/weekly-report/runs', summary: 'Generar ahora (semana indicada o la última completa)', tag: 'platform',
    capability: report, body: R.CreateWeeklyReportRunSchema, response: R.WeeklyReportRunSchema, status: 202,
  }),

  listLearningProposals: route({
    method: 'GET', path: '/platform/learning/proposals', summary: 'Cola de aprendizaje supervisado', tag: 'platform',
    capability: learning, query: R.LearningProposalQuerySchema, response: items(R.LearningProposalSchema),
  }),
  generateLearningProposals: route({
    method: 'POST', path: '/platform/learning/proposals/generate', summary: 'Generar propuestas a partir de conversaciones de la semana', tag: 'platform',
    capability: learning, body: R.GenerateLearningSchema, response: R.GenerateLearningResultSchema,
  }),
  approveLearningProposal: route({
    method: 'POST', path: '/platform/learning/proposals/:proposalId/approve', summary: 'Aprobar (comercial o técnica)', tag: 'platform',
    capability: learning, params: Proposal, body: R.ApproveLearningSchema, response: R.LearningProposalSchema,
  }),
  rejectLearningProposal: route({
    method: 'POST', path: '/platform/learning/proposals/:proposalId/reject', summary: 'Rechazar con motivo', tag: 'platform',
    capability: learning, params: Proposal, body: R.RejectLearningSchema, response: R.LearningProposalSchema,
  }),
  implementLearningProposal: route({
    method: 'POST', path: '/platform/learning/proposals/:proposalId/implement', summary: 'Implementar como NUEVO borrador (nunca publica)', tag: 'platform',
    capability: learning, params: Proposal, body: R.ImplementLearningSchema, response: R.LearningProposalSchema,
  }),
};
